import deepmerge from 'deepmerge';
import type { Config, DeepPartial } from '../default/types';
import defaultConfig, { getFullLocaleFromConfig, environment, customArrayMerge } from '../default';
import { productConfig } from './product/product';
import { installmentCalculatorConfig } from './product/installmentCalculator';
import { dynamicHelp } from './cms/dynamicHelp';

const tenantId = 'lascanaat';

const config = deepmerge<Config, DeepPartial<Config>>(
  defaultConfig,
  {
    host: { name: tenantId, domain: 'lascana.at', appDeeplinkProtocol: 'latapp' },
    clientId: 'LascanaAt',
    i18n: {
      defaultLocale: 'de-AT',
      locales: ['de-AT'],
      languages: ['de'],
    },
    tenantId,
    company: {
      name: 'Lascana',
      seoTitleName: 'OTTO',
    },
    timeZone: 'Europe/Vienna',
    headerSlots: {
      serviceApi: `https://storage.googleapis.com/drupal-pages/${environment}/menus/${tenantId}/<locale>/bucket-service-flyout.json`,
    },
    search: {
      adServer: {
        mode: 'containerOnly',
      },
    },
    abTesting: {
      serverSideEnrollment: {
        activeTestIds: [],
      },
    },
    assets: {
      cmsImage: {
        imageOptimizationEnabled: true,
        uriTransformEndpoint: `https://storage.googleapis.com/drupal-images-${tenantId}/$1/`,
      },
      assetServerHost: 'https://bilder.lascana.at',
    },
    staticContent: {
      apiEndpoints: {
        webappJson: `https://cms.${tenantId}.empirie.<envTld>/<locale>/<path>?_format=webapp_json&flat`,
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    product: productConfig,
    installmentCalculator: installmentCalculatorConfig,
    order: {
      premium: { enabled: false },
      seals: {
        handelsverband: {
          enabled: true,
          url: '/service-hilfe/ueber-uns/auszeichnungen/',
        },
        dqs: {
          enabled: true,
          url: '/service-hilfe/ueber-uns/auszeichnungen/',
        },
        oeonline: {
          enabled: true,
          url: '/service-hilfe/ueber-uns/auszeichnungen/',
        },
      },
      assets: {
        premium: {
          getPremiumImage: ({ device }: { device: string }) =>
            `https://bilder.lascana.at/empiriecom/io/lascanaat/de/21kw10_OTTOup_Checkout_${device}.jpg`,
          getPremiumIcon: () =>
            'https://bilder.lascana.at/empiriecom/io/lascanaat/de/premiumicon.svg',
        },
        gifs: {
          success: {
            url: 'https://bilder.lascana.at/i/empiriecom/io/de/thank_you_1_transparent.gif',
          },
        },
      },
      payback: {
        enabled: false,
        links: {
          partner:
            'https://www.payback.at/partner-websites/otto?emid=6a88b25412fb27427c9b669481ac372a',
          customerData: 'https://www.payback.at/meine-daten',
        },
        cardNumberLength: 16,
        cardNumberHintUrl: '/fragment/inspire/servicelayer/de/payback_kartennummer_io',
      },
      recommendations: {
        wishlist: {
          similarProducts: {
            isEnabled: false,
            type: 'searchReplace',
          },
        },
      },
    },
    footer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    forms: {
      newsletterSettings: {
        sendAdditionalSubscribe: true,
      },
      contact: {
        dynamicHelp,
      },
      newsletterUnsubscribe: {
        useDedicatedSubmitButtonTitleFor: ['unsubscribe|199', 'unsubscribe|299', 'unsubscribe|99'],
        fields: [
          {
            id: 'newsletter',
            formType: 'radio',
            defaultValue: 'unsubscribe|199',
            options: [
              {
                id: 'unsubscribe-otto-lascana',
                value: 'unsubscribe|199',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe.otto-lascana',
              },
              {
                id: 'unsubscribe-lascana',
                value: 'unsubscribe|299',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe.lascana',
              },
              {
                id: 'unsubscribe-otto',
                value: 'unsubscribe|99',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe.otto',
              },
              {
                id: 'pause',
                value: 'pause|-100',
                messageKey: 'forms.newsletter.unsubscribe.type.pause',
              },
            ],
            additionalForm: {
              id: 'pause',
              formType: 'select',
              type: 'pause',
              showOnlyWhenWhatSelected: 'pause',
              messageKeyPlaceholder: 'forms.newsletter.unsubscribe.type.pause.label',
              defaultValue: '7',
              options: [
                {
                  id: 'pause-7',
                  value: '7',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.7',
                },
                {
                  id: 'pause-14',
                  value: '14',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.14',
                },
                {
                  id: 'pause-21',
                  value: '21',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.21',
                },
                {
                  id: 'pause-30',
                  value: '30',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.30',
                },
              ],
            },
          },
        ],
      },
      newsletterSubscribe: {
        de: {
          unsubscribeLink: 'https://www.lascana.at/service-hilfe/newsletter-abmelden',
          voucherLink: 'https://www.lascana.at/service-hilfe/gutscheine-rabatte/',
        },
      },
    },
    banner: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    nlSheetTexts: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/nl-sheet.json`,
      },
    },
    serviceLayer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
        bucketPages: `https://storage.googleapis.com/drupal-pages/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    seoContent: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/inspire-seofiles/<locale>_${tenantId}-<category>.json`,
      },
    },
    survey: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-surveys/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    promotionBanner: {
      apiEndpoints: {
        bucketEmptyPromotions: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/empty-promotion.json`,
        bucketPromotion: `https://storage.googleapis.com/promotion-data/${tenantId}/<language>/<code>.json`,
        bucketContentSnippet: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/<identifier>.json`,
      },
    },
    recommendations: {
      provider: 'empiriecom',
    },
    voucher: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/promotion-data/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    appBanner: {
      enabled: true,
      appUrl: 'https://app.adjust.com/1ljgq46i_1lorbi3j',
    },
    tracking: {
      categoryIdHomepage: '161441',
      soastaId: 'X2HPS-285CA-5MLUR-U7VPY-7E3R4',
      tagmanagerId: 'GTM-W8BJ9R4',
      googleSiteVerificationID: ['OTOyZGCNE4oOQ_91TV_2c3DeGnpNEsqffPwgTlli_Ds'],
      msvalidateID: '313E2E7F8778335B4C29FD80AAAE5469',
      pinterestRichPinVerificationID: '2fe0193630b96620f4dbabcb080e4da1',
      schemaOrgOrganisation: {
        'de-AT': {
          addressCountry: 'AT',
          addressRegion: 'ST',
          addressLocality: 'Graz',
          alternateName: ['LASCANA', 'Lascana Versand'],
          areaServed: 'AT',
          description: 'LASCANA - Bademode, Unterwäsche & Lingerie online kaufen',
          contactEmail: 'kundenservice@lascana.at',
          contactUrl: 'https://www.lascana.at/service-hilfe/kontakt',
          email: 'service@lascana.at',
          founder: 'Jens Fehnders',
          foundingDate: '2002-01-01',
          foundingLocation: 'Hamburg',
          hoursAvailable: ['Mo-Su 7:00-22:00'],
          legalName: 'Otto GmbH',
          logo: `/assets/logos/${tenantId}/logo.svg`,
          memberOf: 'ottogroup',
          name: 'Lascana',
          productSupported: '',
          sameAs: [
            'https://www.facebook.com/lascanaAT',
            'https://www.instagram.com/lascanaofficial/',
          ],
          slogan: 'Make it your world.',
          telephone: '+43-316-60-61-50',
          url: 'https://www.lascana.at',
          postalCode: '8020',
          privacyNoticePath: '/service-hilfe/ueber-uns/datenschutz',
          potentialAction: {
            target: 'https://www.lascana.at/s/',
          },
          streetAddress: 'Alte Poststraße 152',
        },
      },
    },
    externalScript: {
      kuecheCo: {
        key: 'pAYrueynYRcqna7lGLoVT39kF83Wy3PYENp81Yw0QJqeTyzAjXvc1FwGOzCm',
      },
    },
    headerInformation: {
      de: {
        description:
          'Lascana - Onlineshop für Bademode, Dessous & Unterwäsche ✓ aktuelle Trends ✓ Flexible Zahlung ✓ Kauf auf Rechnung » Jetzt bestellen!',
        title: 'Lascana » Onlineshop für Bademode, Dessous & Unterwäsche',
        canonical: 'https://www.lascana.at',
        robots: 'noodp,index,follow',
      },
    },
    // kim is renamed as "customerCentricFeedback"
    customerCentricFeedback: {
      enabled: false,
      tenantId: '1000',
    },
    dynamicYieldTracking: {
      key: '02730caa1ca3c4f3096718ef342ea9b7410b1112bf8765bbd169a7005032ce5e',
    },
    geoCountryLayer: {
      enabled: true,
      flag: 'at',
      alternatives: [
        {
          flag: 'de',
          shops: [{ link: 'https://www.otto.de' }],
        },
        {
          flag: 'ch',
          shops: [{ link: 'https://www.jelmoli-shop.ch' }],
        },
        {
          flag: 'it',
          shops: [{ link: 'https://www.bonprix.it' }],
        },
      ],
    },
  },
  { arrayMerge: customArrayMerge },
);

export const getFullLocale = (localeOrLanguage: string | undefined) =>
  getFullLocaleFromConfig(localeOrLanguage, config);

// eslint-disable-next-line import-x/no-default-export
export default config;
